import animation from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import styled, { css } from 'styled-components';
import { fluid } from '@helpers/fluid';
export const ButtonText = styled.span `
  position: relative;
  text-rendering: optimizeLegibility;
  transform: translate3d(0, 0, 0);
  will-change: transform;
  z-index: 2;
`;
export const ButtonIcon = styled.i `
  position: absolute;
  top: 50%;
  display: inline-block;
  transform: translateY(-50%);
  transition: transform 0.3s ${animation.easeInOutCubic};
  z-index: 2;
  width: 18px;
  height: 14px;

  &[data-large-icon='true'] {
    height: 24px;
    width: 87px;
  }

  @media ${from(Device.Tablet)} {
    width: 20px;
    height: 16px;

    &[data-large-icon='true'] {
      height: 36px;
      width: 115px;
    }
  }

  ${({ position, size }) => {
    switch (size) {
        default:
        case 'default':
        case 'extra-large': {
            switch (position) {
                case 'before': {
                    return css `
              left: 16px;
            `;
                }
                default:
                case 'after': {
                    return css `
              right: 16px;
            `;
                }
            }
        }
        case 'small': {
            switch (position) {
                case 'before': {
                    return css `
              left: 6px;

              @media ${from(Device.Tablet)} {
                left: 16px;
              }
            `;
                }
                default:
                case 'after': {
                    return css `
              right: 6px;

              @media ${from(Device.Tablet)} {
                right: 16px;
              }
            `;
                }
            }
        }
        case 'small-mobile': {
            switch (position) {
                case 'before': {
                    return css `
              left: 6px;

              @media ${from(Device.Tablet)} {
                left: 16px;
              }
            `;
                }
                default:
                case 'after': {
                    return css `
              right: 6px;

              @media ${from(Device.Tablet)} {
                right: 16px;
              }
            `;
                }
            }
        }
    }
}}

  ${({ icon }) => {
    switch (icon) {
        case 'pin':
            return css `
          height: 18px;

          @media ${from(Device.Tablet)} {
            height: 22px;
          }
        `;
        default:
            break;
    }
}}

  ${({ limitIconMovement }) => limitIconMovement &&
    css `
      transform: translateY(-50%) !important;
    `}

  svg {
    display: block;
    width: 100%;
    height: 100%;

    ${({ rotateAngle }) => rotateAngle &&
    css `
        transform: rotate(${rotateAngle}deg);
      `}
  }
`;
export const BaseButton = css `
  box-shadow: ${brand.bs()};
  ${fonts.lato.semiBold()};
  position: relative;
  display: inline-block;
  margin: 0;
  border: 0;

  --padding-y: 10px;
  --padding-x: 26px;
  --icon-padding: 20px;
  --icon-padding-large: 97px;
  --icon-padding-before: 0px;
  --icon-padding-after: 0px;

  padding: var(--padding-y) calc(var(--padding-x) + var(--icon-padding-after)) var(--padding-y) calc(var(--padding-x) + var(--icon-padding-before));

  &:not([data-icon='none']) {
    &[data-icon-position='before'],
    &[data-icon-position='both'] {
      --icon-padding-before: var(--icon-padding);
    }

    &[data-icon-position='after'],
    &[data-icon-position='both'] {
      --icon-padding-after: var(--icon-padding);
    }

    &[data-large-icon-position='before'],
    &[data-large-icon-position='both'] {
      --icon-padding-before: var(--icon-padding-large);
    }

    &[data-large-icon-position='after'],
    &[data-large-icon-position='both'] {
      --icon-padding-after: var(--icon-padding-large);
    }
  }

  &[data-size='small'],
  &[data-size='small-mobile'] {
    --padding-y: 9px;
    --padding-x: ${fluid(7, 12, Device.Mobile, Device.Tablet)};
  }

  @media ${from(Device.Tablet)} {
    --icon-padding: 24px;
    --icon-padding-large: 125px;
    &[data-size='default'],
    &[data-size='extra-large'],
    &[data-size='small-mobile'] {
      --padding-y: 15px;
      --padding-x: 26px;
    }
  }

  overflow: hidden;
  text-transform: uppercase;
  text-decoration: none;
  transform: translate3d(0, 0, 0);
  transition: box-shadow 0.3s ${animation.easeInOutCubic}, transform 0.3s ${animation.easeInOutCubic}, opacity 0.3s ${animation.easeInOutCubic};
  will-change: transform;
  user-select: none;
  contain: paint;
  cursor: pointer;
  opacity: 1;

  &[data-progress='true'] {
    cursor: progress;
    opacity: 0.65;
  }

  ${({ cursor }) => cursor
    ? css `
          cursor: ${cursor} !important;
        `
    : null}

  &[data-size="default"] {
    font-size: 15px;
    line-height: 1.2em;
    letter-spacing: ${fonts.ls(100)};
  }

  &[data-size='small-mobile'] {
    font-size: 11px;
    line-height: 1.2em;
    letter-spacing: ${fonts.ls(50)};

    @media ${from(Device.Tablet)} {
      font-size: 15px;
      letter-spacing: ${fonts.ls(100)};
    }
  }

  &[data-size='small'] {
    font-size: 13px;
    line-height: 1.2em;
    letter-spacing: ${fonts.ls(50)};
  }

  &[data-size='extra-large'] {
    font-size: 36px;
    line-height: 3.2em;
  }

  ${(props) => {
    switch (props.theme) {
        default:
        case 'green': {
            return css `
          background: ${brand.green.light};
          color: ${brand.grey[20]};

          &:before {
            background: ${brand.green.dark};
          }

          &:focus,
          &:hover {
            color: ${brand.grey[20]};
          }
        `;
        }
        case 'white': {
            return css `
          background: ${brand.grey[95]};
          color: ${brand.grey[20]};

          &:before {
            background: ${brand.white};
          }

          &:focus,
          &:hover {
            color: ${brand.grey[20]};
          }
        `;
        }
        case 'grey': {
            return css `
          background: ${brand.grey[25]};
          color: ${brand.white};

          &:before {
            background: ${brand.grey[20]};
          }

          &:focus,
          &:hover {
            color: ${brand.white};
          }

          ${ButtonIcon} svg {
            color: ${brand.white};
          }
        `;
        }
        case 'red': {
            return css `
          background: ${brand.red.dark};
          color: ${brand.white};

          &:before {
            background: ${brand.red.base};
          }

          &:focus,
          &:hover {
            color: ${brand.white};
          }

          ${ButtonIcon} svg {
            color: ${brand.white};
          }
        `;
        }
        case 'red-outline': {
            return css `
          background: ${brand.white};
          color: ${brand.red.base};
          border: 1px solid ${brand.red.base};
          box-shadow: none;
          cursor: inherit;

          &:before {
            background: ${brand.white};
            display: none;
          }

          &:focus,
          &:hover {
            color: ${brand.red.base};
          }
        `;
        }
        case 'out-of-stock-dark': {
            return css `
          background: ${brand.pwpBlack};
          color: ${brand.green.base};
          border: 2px solid ${brand.green.base};
          box-shadow: none;
          cursor: inherit;

          &:before {
            background: ${brand.pwpBlack};
            display: none;
          }

          &:focus,
          &:hover {
            color: ${brand.green.base};
          }
        `;
        }
        case 'out-of-stock-grey': {
            return css `
          background: ${brand.grey[70]};
          color: ${brand.grey[40]};
          border: 2px solid ${brand.grey[70]};
          box-shadow: none;
          cursor: inherit;

          &:before {
            display: none;
          }

          &:focus,
          &:hover {
            color: ${brand.grey[40]};
          }
        `;
        }
        case 'paypal': {
            return css `
          background: ${brand.paypal.payBlue};
          color: ${brand.paypal.white};

          &:before {
            background: ${brand.paypal.palBlue};
          }

          &:focus,
          &:hover {
            color: ${brand.paypal.white};
          }

          ${ButtonIcon} svg {
            color: ${brand.paypal.white};
          }
        `;
        }
        case 'unavailable': {
            return css `
          background: ${brand.grey[60]};
          color: ${brand.paypal.white};
          box-shadow: none;
          cursor: inherit;

          &:before {
            background: ${brand.grey[60]};
          }

          &:focus,
          &:hover {
            color: ${brand.paypal.white};
          }

          ${ButtonIcon} svg {
            color: ${brand.paypal.white};
          }
        `;
        }
        case 'transparent': {
            return css `
          box-shadow: none;
          background: transparent;
          color: ${brand.grey[20]};

          &:before {
            background: transparent;
          }

          &:focus,
          &:hover {
            color: ${brand.grey[20]};
          }
        `;
        }
        case 'transparent-white': {
            return css `
          box-shadow: none;
          background: transparent;
          color: ${brand.white};

          &:before {
            background: transparent;
          }

          &:focus,
          &:hover {
            color: ${brand.white};
          }
        `;
        }
        case 'black': {
            return css `
          background: ${brand.grey[10]};
          color: ${brand.white};

          &:before {
            background: ${brand.grey[25]};
          }

          &:focus,
          &:hover {
            color: ${brand.white};
          }

          ${ButtonIcon} svg {
            color: ${brand.white};
          }
        `;
        }
    }
}}

  ${({ error, theme, reverseIconAnimation, disableVerticalMovementOnHover }) => {
    if (error) {
        return css `
        border: 1px solid ${brand.red.base};
        color: ${brand.red.base};
      `;
    }
    if (theme === 'red-outline' || theme === 'unavailable' || theme === 'out-of-stock-dark' || theme === 'out-of-stock-grey') {
        return css ``;
    }
    return css `
      &:focus,
      &:hover {
        box-shadow: ${brand.bs(true)};
        text-decoration: none;

        ${disableVerticalMovementOnHover ? null : 'transform: translate(0, -2px);'}

        &::before {
          transform: translate(0, -50%) translate(-80px, 40px) scale(1.75);
        }

        ${ButtonIcon} {
          transform: translateY(-50%) translateX(${reverseIconAnimation ? '-4px' : '4px'});
        }
      }
    `;
}}

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    display: block;
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    border-radius: 50%;
    transform: translate(0, -50%) translate(-80px, 40px) scale(1);
    transition: transform 0.5s ${animation.easeInOutCubic};
    z-index: 1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ centreText }) => centreText
    ? css `
          text-align: center;
        `
    : css `
          text-align: left;
        `}
`;
export const ButtonElement = styled.button.attrs({ type: 'button' }) `
  ${BaseButton};
`;
export const LinkElement = styled.a `
  ${BaseButton};
`;
